import { useEffect, useRef, useState } from "react";

/**
 * Lazy Google Maps embed for location pages.
 *
 * The iframe is only inserted once the block scrolls near the viewport, so a
 * third-party frame never competes with LCP on mobile. No API key is required
 * for the public embed endpoint.
 */
export function LocationMap({
  city,
  region = "TX",
  latLng,
  className = "",
}: {
  city: string;
  region?: string;
  latLng?: { lat: number; lng: number };
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || show) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [show]);

  const query = latLng
    ? `${latLng.lat},${latLng.lng}&z=11`
    : encodeURIComponent(`${city}, ${region}`);
  const src = `https://www.google.com/maps?q=${query}&output=embed`;
  const title = `Map of the ${city}, ${region} service area covered by Optimize Plus`;

  return (
    <figure ref={ref} className={`overflow-hidden rounded-2xl border border-border ${className}`}>
      <div className="relative aspect-[16/9] bg-card/60">
        {show ? (
          <iframe
            title={title}
            src={src}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="absolute inset-0 h-full w-full border-0"
            allowFullScreen={false}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-xs uppercase tracking-widest text-muted-foreground">
            Loading {city} service-area map…
          </div>
        )}
      </div>
      <figcaption className="px-4 py-3 text-xs text-muted-foreground">
        Optimize Plus serves {city} and the surrounding {region} corridor from our South Austin
        office at 1431 Salem Meadow Cir, Austin, TX 78745.
      </figcaption>
    </figure>
  );
}
